/**
 * Actions inter-Totems — espace unique de réception, suivi des envois et journal.
 * Design System SEIZO : information opérationnelle compacte et traçable.
 */
import React, { useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { useStores } from '../../../../stores/Context';
import Overlay from './Overlay';
import styles from './IntertotemActions.module.scss';

const STATUS = {
    brouillon: 'Brouillon',
    envoyee: 'Envoyée',
    recue: 'Reçue',
    acceptee: 'Acceptée',
    refusee: 'Refusée',
    en_cours: 'En cours',
    resolue: 'Résolue',
    cloturee: 'Clôturée',
};

const EVENT = {
    action_created: 'Action créée',
    sent: 'Action envoyée',
    received: 'Action reçue',
    accepted: 'Action acceptée',
    refused: 'Action refusée',
    started: 'Action démarrée',
    resolved: 'Action résolue',
    closed: 'Action clôturée',
    reopened: 'Action réouverte',
    assigned: 'Responsable modifié',
    planned: 'Planning local associé',
    source_task_moved_to_sent_lane: 'Tâche source rangée dans Actions envoyées',
    reminded: 'Relance envoyée',
    follow_up_added: 'Suivi ajouté',
};

const dateTime = (value) => value
    ? new Date(value).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
    : '—';

const actorLabel = (event) => {
    if (event.actor?.first_name || event.actor?.last_name) {
        return `${event.actor.first_name || ''} ${event.actor.last_name || ''}`.trim();
    }
    return event.actor?.type === 'Entreprise'
        ? 'Identité utilisateur indisponible'
        : 'Système';
};

const nextTransitions = (action, isDestinationAction) => {
    if (isDestinationAction) {
        if (action.status === 'recue') return [{ status: 'acceptee', label: 'Accepter' }, { status: 'refusee', label: 'Refuser', danger: true }];
        if (action.status === 'acceptee') return [{ status: 'en_cours', label: 'Démarrer' }];
        if (action.status === 'en_cours') return [{ status: 'resolue', label: 'Marquer résolue' }];
        if (action.status === 'resolue') return [{ status: 'cloturee', label: 'Clôturer' }];
        return [];
    }
    if (['refusee', 'resolue', 'cloturee'].includes(action.status)) {
        return [{ status: 'en_cours', label: 'Réouvrir' }];
    }
    return [];
};

const IntertotemActions = observer(() => {
    const { PlanningActionsStore } = useStores();
    const [tab, setTab] = useState('received');
    const [comment, setComment] = useState('');
    const [assigneeId, setAssigneeId] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const receivedActions = PlanningActionsStore.intertotemActionsReceived.filter(action => !action.local_task);
    const plannedActions = PlanningActionsStore.intertotemActionsReceived.filter(action => action.local_task);
    const actions = tab === 'received'
        ? receivedActions
        : tab === 'planned'
            ? plannedActions
            : PlanningActionsStore.intertotemActionsSent;
    const selected = PlanningActionsStore.intertotemActionSelected;
    const isDestinationAction = tab === 'received' || tab === 'planned';
    const transitions = useMemo(() => selected ? nextTransitions(selected, isDestinationAction) : [], [selected, isDestinationAction]);

    const openAction = async (actionId) => {
        setError('');
        setComment('');
        setBusy(true);
        try {
            const action = await PlanningActionsStore.ouvrirActionIntertotem(actionId);
            setAssigneeId(action?.assignee?.id ? String(action.assignee.id) : '');
        } catch (err) {
            setError(err.response?.data?.message || 'Impossible d’ouvrir cette action.');
        } finally {
            setBusy(false);
        }
    };

    const applyTransition = async (status) => {
        if (!selected) return;
        const requiresComment = ['refusee', 'resolue'].includes(status)
            || (status === 'en_cours' && ['refusee', 'resolue', 'cloturee'].includes(selected.status));
        if (requiresComment && !comment.trim()) {
            setError('Un commentaire est obligatoire pour cette étape.');
            return;
        }
        setBusy(true);
        setError('');
        try {
            await PlanningActionsStore.transitionActionIntertotem(selected.id, status, comment.trim() || null, selected.version);
            setComment('');
            await openAction(selected.id);
        } catch (err) {
            setError(err.response?.data?.message || 'La transition a échoué.');
        } finally {
            setBusy(false);
        }
    };

    const assign = async () => {
        if (!selected) return;
        setBusy(true);
        setError('');
        try {
            await PlanningActionsStore.assignerActionIntertotem(selected.id, assigneeId || null, comment.trim() || null);
            await openAction(selected.id);
        } catch (err) {
            setError(err.response?.data?.message || 'La mise à jour du responsable a échoué.');
        } finally {
            setBusy(false);
        }
    };

    const plan = async () => {
        if (!selected) return;
        setBusy(true);
        setError('');
        try {
            await PlanningActionsStore.planifierActionIntertotem(selected.id, comment.trim() || null);
            await openAction(selected.id);
        } catch (err) {
            setError(err.response?.data?.message || 'La planification locale a échoué.');
        } finally {
            setBusy(false);
        }
    };

    const openLocalTask = () => {
        PlanningActionsStore.ouvrirTacheLocaleIntertotem();
    };

    const addFollowUp = async () => {
        if (!selected) return;
        if (!comment.trim()) {
            setError('Saisissez votre information de suivi avant de l’ajouter au journal.');
            return;
        }

        setBusy(true);
        setError('');
        try {
            await PlanningActionsStore.ajouterSuiviActionIntertotem(selected.id, comment.trim());
            setComment('');
            await openAction(selected.id);
        } catch (err) {
            setError(err.response?.data?.message || 'L’ajout du suivi a échoué.');
        } finally {
            setBusy(false);
        }
    };

    const remind = async () => {
        if (!selected) return;
        setBusy(true);
        setError('');
        try {
            await PlanningActionsStore.relancerActionIntertotem(selected.id, comment.trim() || null);
            setComment('');
            await openAction(selected.id);
        } catch (err) {
            setError(err.response?.data?.message || 'La relance a échoué.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Overlay titre="Actions échangées" onClose={() => PlanningActionsStore.closeModal()} width="min(1100px, 94vw)">
            <div className={styles.root}>
                <p className={styles.intro}>Votre espace unique pour recevoir, planifier et suivre les actions entre Totems. Le journal conserve chaque étape du cycle de vie.</p>
                <div className={styles.flow} aria-label="Parcours d'une action reçue">
                    <span><b>1</b> Recevoir</span>
                    <span><b>2</b> Accepter et planifier</span>
                    <span><b>3</b> Suivre jusqu’à la clôture</span>
                </div>
                <div className={styles.tabs} role="tablist" aria-label="Actions inter-Totems">
                    <button className={tab === 'received' ? styles.activeTab : ''} onClick={() => { setTab('received'); PlanningActionsStore.intertotemActionSelected = null; }}>
                        À traiter <span>{receivedActions.length}</span>
                    </button>
                    <button className={tab === 'planned' ? styles.activeTab : ''} onClick={() => { setTab('planned'); PlanningActionsStore.intertotemActionSelected = null; }}>
                        Planifiées <span>{plannedActions.length}</span>
                    </button>
                    <button className={tab === 'sent' ? styles.activeTab : ''} onClick={() => { setTab('sent'); PlanningActionsStore.intertotemActionSelected = null; }}>
                        Suivi des envois <span>{PlanningActionsStore.intertotemActionsSent.length}</span>
                    </button>
                </div>

                <div className={styles.workspace}>
                    <section className={styles.list} aria-label={tab === 'received' ? 'Actions à traiter' : tab === 'planned' ? 'Actions planifiées' : 'Actions envoyées'}>
                        {PlanningActionsStore.intertotemActionsLoading && <p className={styles.muted}>Chargement…</p>}
                        {!PlanningActionsStore.intertotemActionsLoading && actions.length === 0 && <p className={styles.empty}>{tab === 'received' ? 'Aucune action à traiter pour ce Totem.' : tab === 'planned' ? 'Aucune action planifiée pour ce Totem.' : 'Aucune action envoyée pour ce Totem.'}</p>}
                        {actions.map(action => (
                            <button key={action.id} className={`${styles.actionCard} ${selected?.id === action.id ? styles.selected : ''}`} onClick={() => openAction(action.id)}>
                                <div className={styles.cardTop}>
                                    <strong>{action.title}</strong>
                                    <span className={`${styles.status} ${styles[`status_${action.status}`]}`}>{STATUS[action.status] || action.status}</span>
                                </div>
                                <span className={styles.counterpart}>{isDestinationAction ? `De : ${action.source_totem?.name || 'Totem source'}` : `Pour : ${action.destination_totem?.name || 'Totem destinataire'}`}</span>
                                <span className={styles.cardDate}>Mis à jour {dateTime(action.updated_at)}</span>
                                {action.reminder_count > 0 && <span className={styles.reminder}>Relancé {action.reminder_count} fois</span>}
                            </button>
                        ))}
                    </section>

                    <section className={styles.detail} aria-live="polite">
                        {!selected && <div className={styles.emptyDetail}>Sélectionnez une action pour consulter son détail et son journal.</div>}
                        {selected && <>
                            <div className={styles.detailHeader}>
                                <div>
                                    <span className={styles.module}>{selected.source_module === 'rsp' ? 'Résolution de problèmes' : 'Planning d’Actions'}</span>
                                    <h3>{selected.title}</h3>
                                </div>
                                <span className={`${styles.status} ${styles[`status_${selected.status}`]}`}>{STATUS[selected.status] || selected.status}</span>
                            </div>
                            {selected.description && <p className={styles.description}>{selected.description}</p>}
                            <div className={styles.metaGrid}>
                                <span><b>Émetteur</b>{selected.source_totem?.name || '—'}</span>
                                <span><b>Destinataire</b>{selected.destination_totem?.name || '—'}</span>
                                <span><b>Échéance</b>{selected.due_date || 'Non définie'}</span>
                                <span><b>Relances</b>{selected.reminder_count || 0}</span>
                            </div>

                            <label className={styles.commentLabel}>Commentaire de suivi
                                <textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Motif de refus, résolution, réouverture ou information de suivi…" maxLength={4000} />
                            </label>
                            {isDestinationAction && <p className={styles.followUpHelp}>Ajoutez une information au journal sans modifier le statut de l’action.</p>}
                            {error && <p className={styles.error}>{error}</p>}

                            {isDestinationAction && ['acceptee', 'en_cours'].includes(selected.status) && <div className={styles.inlineControl}>
                                <select value={assigneeId} onChange={e => setAssigneeId(e.target.value)} disabled={busy}>
                                    <option value="">Aucun responsable</option>
                                    {PlanningActionsStore.membres.map(member => <option key={member.id} value={member.id}>{member.prenom} {member.nom}</option>)}
                                </select>
                                <button onClick={assign} disabled={busy}>Enregistrer le responsable</button>
                            </div>}

                            <div className={styles.actions}>
                                {isDestinationAction && <button className={styles.followUpButton} onClick={addFollowUp} disabled={busy || !comment.trim()}>Ajouter ce suivi au journal</button>}
                                {isDestinationAction && ['acceptee', 'en_cours'].includes(selected.status) && !selected.local_planning && <button onClick={plan} disabled={busy}>Planifier ici</button>}
                                {isDestinationAction && selected.local_task && <button className={styles.secondary} onClick={openLocalTask}>Ouvrir dans mon Planning</button>}
                                {transitions.map(transition => <button key={transition.status} className={transition.danger ? styles.danger : ''} onClick={() => applyTransition(transition.status)} disabled={busy}>{transition.label}</button>)}
                                {tab === 'sent' && !['brouillon', 'refusee', 'cloturee'].includes(selected.status) && <button className={styles.secondary} onClick={remind} disabled={busy}>Relancer le Totem</button>}
                            </div>

                            <div className={styles.timeline}>
                                <h4>Journal de l’action</h4>
                                {selected.events?.length === 0 && <p className={styles.muted}>Aucun événement disponible.</p>}
                                {selected.events?.map(event => <article className={styles.event} key={event.id}>
                                    <span className={styles.eventDot} />
                                    <div><strong>{EVENT[event.type] || event.type}</strong><span>{dateTime(event.created_at)} · {actorLabel(event)}</span>{event.comment && <p>{event.comment}</p>}</div>
                                </article>)}
                            </div>
                        </>}
                    </section>
                </div>
            </div>
        </Overlay>
    );
});

export default IntertotemActions;
